import { useEffect, useState } from 'react'
import {
  Users,
  TrendingUp,
  DollarSign,
  Clock,
  Copy,
  RefreshCw,
  Trophy,
  History as HistoryIcon,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { usePartnerStore } from '@/store/partnerStore'
import { useAuthStore } from '@/store/authStore'
import commissionService from '@/services/commissionService'
import referralService from '@/services/referralService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import KpiCard from '@/components/KpiCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const activityIcon = (type) => {
  switch (type) {
    case 'referral':
      return <Users size={16} className="text-primary" />
    case 'commission':
      return <Trophy size={16} className="text-success-700 dark:text-success-300" />
    default:
      return <HistoryIcon size={16} className="text-subtle" />
  }
}

export default function Dashboard() {
  const { partner } = useAuthStore()
  const { dashboard, fetchDashboard, isLoading } = usePartnerStore()
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchDashboard()
    setRefreshing(false)
    toast.success('Dashboard actualizado')
  }

  const handleCopyLink = async () => {
    const link = referralService.generateReferralLink(partner?.referralCode || '')
    const ok = await referralService.copyToClipboard(link)
    if (ok) toast.success('Link copiado')
  }

  const referralLink = referralService.generateReferralLink(partner?.referralCode || '')

  const kpis = [
    {
      title: 'Total referidos',
      value: dashboard?.metrics?.totalReferrals ?? 0,
      icon: <Users size={20} />,
      tone: 'primary',
      delta: dashboard?.metrics?.referralsThisMonth ?? 0,
      deltaLabel: 'este mes',
    },
    {
      title: 'Activos',
      value: dashboard?.metrics?.activeReferrals ?? 0,
      icon: <TrendingUp size={20} />,
      tone: 'success',
      delta: dashboard?.metrics?.conversionRate
        ? `${dashboard.metrics.conversionRate.toFixed(1)}%`
        : '0%',
      deltaLabel: 'conversión',
    },
    {
      title: 'Comisión del mes',
      value: commissionService.formatCurrency(dashboard?.metrics?.currentMonthCommission ?? 0),
      icon: <DollarSign size={20} />,
      tone: 'accent',
      delta: commissionService.formatCurrency(dashboard?.metrics?.totalEarnings ?? 0),
      deltaLabel: 'total generado',
    },
    {
      title: 'Pendiente de pago',
      value: commissionService.formatCurrency(dashboard?.metrics?.pendingPayout ?? 0),
      icon: <Clock size={20} />,
      tone: 'warning',
      delta: dashboard?.metrics?.approvedCommissions ?? 0,
      deltaLabel: 'comisiones aprobadas',
    },
  ]

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        description={`Bienvenido de nuevo, ${partner?.organizationName || 'Partner'}`}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Actualizar"
          >
            <RefreshCw size={14} className={cn(refreshing && 'animate-spin')} />
            <span className="hidden sm:inline">Actualizar</span>
          </Button>
        }
      />

      {/* Referral link card destacada */}
      <Card className="bg-primary text-primary-foreground border-primary">
        <CardContent className="flex flex-col gap-3 p-4 sm:p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-caption uppercase tracking-wide text-primary-100/80">
              Tu enlace de referido
            </p>
            <p className="mt-1 text-body-sm font-mono break-all text-primary-foreground">
              {referralLink}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <code className="rounded-md bg-card px-3 py-1.5 text-body font-mono font-semibold text-primary tracking-wider">
              {partner?.referralCode || 'XXXXXX'}
            </code>
            <Button
              type="button"
              variant="secondary"
              size="icon"
              onClick={handleCopyLink}
              aria-label="Copiar link"
              className="h-10 w-10 bg-primary-foreground/15 text-primary-foreground hover:bg-primary-foreground/25 backdrop-blur"
            >
              <Copy size={16} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs: stack vertical mobile / grid 2x2 sm / 4-col xl */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {kpis.map((k, i) => (
          <KpiCard key={i} {...k} loading={isLoading && !dashboard} />
        ))}
      </div>

      {/* Activity + Plan */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-h3">Actividad reciente</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : dashboard?.recentActivity?.length > 0 ? (
              <ul className="divide-y divide-line">
                {dashboard.recentActivity.map((activity, idx) => (
                  <li key={idx} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                    <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md bg-surface-muted shrink-0">
                      {activityIcon(activity.type)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-body-sm text-foreground">{activity.description}</p>
                      <p className="text-caption text-subtle mt-0.5">
                        {new Date(activity.date).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-body-sm text-subtle">No hay actividad reciente</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-h3">Tu plan de comisiones</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-32" />
            ) : (
              <dl className="space-y-4">
                <div>
                  <dt className="text-caption text-subtle">Comisión base</dt>
                  <dd className="font-display text-h1 font-semibold text-primary">
                    {dashboard?.partner?.commissionConfig?.defaultPercentage ||
                      partner?.commissionConfig?.defaultPercentage ||
                      20}
                    %
                  </dd>
                </div>
                <div>
                  <dt className="text-caption text-subtle">Duración</dt>
                  <dd className="text-body font-medium text-foreground">
                    {(dashboard?.partner?.commissionConfig?.recurringMonths ??
                      partner?.commissionConfig?.recurringMonths) === 0
                      ? 'Indefinida'
                      : `${
                          dashboard?.partner?.commissionConfig?.recurringMonths ||
                          partner?.commissionConfig?.recurringMonths ||
                          12
                        } meses`}
                  </dd>
                </div>
                <div>
                  <dt className="text-caption text-subtle">Mínimo para pago</dt>
                  <dd className="text-body font-medium text-foreground">
                    {commissionService.formatCurrency(
                      (dashboard?.paymentInfo?.minPayout ??
                        partner?.paymentInfo?.minPayout ??
                        10000) / 100
                    )}
                  </dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}
